import { CaseCalculationAlternativePart, CaseCalculationPart } from '../../../interfaces/Case';
import { IReferential } from '../../../interfaces/IReferential';
import { GridColumnFor, FiltersStateFor, SortStateFor } from '../../../types/grid-filter.types';

export type { RangeFilter, DateRangeFilter, ColumnFilter } from '../../../types/grid-filter.types';
export { ColumnType, isRangeFilter, isDateRangeFilter } from '../../../types/grid-filter.types';

// Selection + recommendation state written onto a variant group (parent + bundle children).
export interface VariantSelectionState {
  isVariantSelected: boolean;
  isSelectedAlternativePart: boolean;
  groupedUniqueId: string | null;
}

export interface CalculationActions {
  // Registers a variant row's DOM node so the optimize flows can scroll to it.
  registerRow: (alternativePartId: number, el: HTMLElement | null) => void;
  toggleExpand: (partId: number) => void;
  interogate: (part: CaseCalculationPart) => Promise<void>;

  interogateAlternativeParts: (part: CaseCalculationPart) => void;
  interogateAll: () => void;
  resetAlternatives: () => void;

  toggleSelect: (partId: number, alternativePart: CaseCalculationAlternativePart) => void;

  setRecommended: (partId: number, alternativePart: CaseCalculationAlternativePart) => void;
  clearRecommendation: (partId: number) => void;
  unselectPart: (partId: number) => void;
  unselectAll: () => void;
  deletePart: (partId: number) => void;
  sendNotificationToSupport: (part: CaseCalculationPart) => Promise<void>;
  addAlternativePart: (partId: number, part: CaseCalculationAlternativePart) => void;
  importOriginalParts: (parts: CaseCalculationPart[]) => void;
  importAlternativeParts: (parts: CaseCalculationAlternativePart[]) => void;
  updateGuideNumber: (partId: number, guideNumber: string) => void;
  updatePieceNr: (partId: number, pieceNr: number) => void;
  updateOriginalPrice: (partId: number, value: number) => void;
  updateCarPartCategory: (partId: number, value: IReferential | null) => void;
  updateAlternativePartDescription: (partId: number, value: string) => void;
  canOptimize: () => boolean;
  openOptimize: () => void;
  optimizeByBestPrice: () => void;
  optimizeByBestQuality: () => void;
  applyOptimal: (variants: number) => void;
  isOptimizingLoading: boolean;
  isApplyingOptimalLoading: boolean;
  addCalculationPart: () => void;
  copyCalculationPart: (part: CaseCalculationPart) => void;
  logStockOverrideConfirm: (
    part: CaseCalculationPart,
    alternativePart: CaseCalculationAlternativePart
  ) => void;
}

export interface SaveValidationResult<T extends CaseCalculationPart = CaseCalculationPart> {
  // The selected parts with their error flags refreshed (unitsError / descriptionError).
  validated: T[];
  errorString: string;
  decimalError: boolean;
  descriptionError: boolean;
  zeroPieceNrError: boolean;
}

export interface CalculationTotals {
  originalPartsAmount: number;
  alternativePartsAmount: number;
  originalAmountWithoutTVA: number;
  alternativeAmountWithoutTVA: number;
  originalSmallPartsAmount: number;
  alternativeSmallPartsAmount: number;
  partsSavingPct: number;
  smallPartsSavingPct: number;
  totalSavingPct: number;
  savingValue: number;
}

export enum ColumnKey {
  Code = 'code',
  Name = 'name',
  NetPrice = 'netPrice',
  Price = 'price',
  Quality = 'quality',
  Delivery = 'delivery',
  Producer = 'producer',
  Supplier = 'supplier',
  Quantity = 'quantity',
  Location = 'location',
  Difference = 'difference'
}

export type GridColumn = GridColumnFor<ColumnKey>;
export type FiltersState = FiltersStateFor<ColumnKey>;
export type SortState = SortStateFor<ColumnKey>;

export type BulkSelectionMode = 'optimize' | 'optimal' | 'quality' | 'price' | null;

export type InterrogationSupplierDto = {
  supplierId: number | null;
  quality: IReferential | null;
};
